import React from 'react'
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// 履歴（history）はアクション種別ごとにスコアの記録形式がバラバラ（例: 捕獲は変更前の状態、
// ミッション提出は加点量、締切は罰点対象チーム一覧）で、単純に record.team.score を読むだけでは
// 大半のスコア変動（特に捕獲によるもの）を拾えない。そのため、teamsの初期スコアを起点に
// 各アクションのスコア増減を時系列順に積み上げてグラフ用データを再構築する。
const buildScoreTimeline = (history, teams, missions) => {
  const teamNameToIndex = new Map(teams.map((team, index) => [team.name, index]));
  const currentScores = teams.map((team) => Number(team.score) || 0);

  const labels = [];
  const datasets = teams.map((team, index) => ({
    id: index,
    label: team.name,
    data: [],
    borderColor: team.color,
    fill: false
  }));

  const recordPoint = (time) => {
    labels.push(new Date(time).toISOString());
    datasets.forEach((dataset, index) => {
      dataset.data.push(currentScores[index]);
    });
  };

  history.forEach((record) => {
    switch (record.actionNum) {
      // 捕獲：記録されているのは捕獲直前(逃走/鬼)の状態のため、そこから±100点を算出（0未満にはならない）
      case 7: {
        if (!Array.isArray(record.teams)) break;

        record.teams.forEach((teamBefore) => {
          const index = teamNameToIndex.get(teamBefore.name);
          if (index === undefined) return;

          currentScores[index] = Number(teamBefore.role) === 0
            ? Math.max(0, currentScores[index] - 100) // 逃走中→捕まった
            : currentScores[index] + 100; // 鬼→捕まえた
        });

        recordPoint(record.time);
        break;
      }

      // コントローラーによる手動更新：確定後のスコアで上書き
      case 1: {
        if (currentScores[record.teamId] === undefined) break;
        currentScores[record.teamId] = Number(record.team?.score) || 0;
        recordPoint(record.time);
        break;
      }

      // アイテムの使用：消費後のスコアで上書き
      case 5: {
        if (currentScores[record.teamId] === undefined) break;
        currentScores[record.teamId] = Number(record.team?.score) || 0;
        recordPoint(record.time);
        break;
      }

      // ミッション提出：addScore分を加点
      case 4: {
        if (currentScores[record.teamId] === undefined) break;
        currentScores[record.teamId] += Number(record.addScore) || 0;
        recordPoint(record.time);
        break;
      }

      // ミッション提出の取消し：提出時の加点を取り消す
      case 10: {
        if (currentScores[record.teamId] === undefined) break;
        const mission = missions ? missions[record.missionId] : null;
        currentScores[record.teamId] -= Number(mission?.score) || 0;
        recordPoint(record.time);
        break;
      }

      // ミッション締切：未提出チームを罰点分減点（0未満にはならない）
      case 12: {
        if (!Array.isArray(record.noSubmitTeams)) break;
        const penalty = Number(record.mission?.penalty) || 0;

        record.noSubmitTeams.forEach((team) => {
          const index = teamNameToIndex.get(team.name);
          if (index === undefined) return;
          currentScores[index] = Math.max(0, currentScores[index] - penalty);
        });

        recordPoint(record.time);
        break;
      }

      // 最終ランキング確定：確定した最終スコアで上書き（それまでの積み上げ誤差を防ぐ）
      case 11: {
        if (!Array.isArray(record.ranking)) break;

        record.ranking.forEach((team) => {
          if (currentScores[team.teamId] === undefined) return;
          currentScores[team.teamId] = Number(team.score) || 0;
        });

        recordPoint(record.time);
        break;
      }

      default:
        break;
    }
  });

  return { labels, datasets };
};

const ScoreGraph = ({history, teams, missions}) => {
  const [data, setData] = React.useState({
    labels: [],
    datasets: []
  });

  React.useEffect(() => {
    // 必要なデータが存在しない場合は何もしない
    if(!history || !teams || history.length === 0 || teams.length === 0) {
      setData({ labels: [], datasets: [] });
      return;
    }

    setData(buildScoreTimeline(history, teams, missions));
  }, [history, teams, missions]);

  return (
    <div style={{ width: '100%', height: '500px' }}>
      <Line
        datasetIdKey='id'
        data={data}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: 'スコアグラフ'
            }
          },
          scales: {
              x: {
                  type: 'time', // ここで時刻軸を指定
                  time: {
                      unit: 'hour',
                      displayFormats: { // 各単位での表示形式
                          hour: 'HH:mm',
                          minute: 'HH:mm',
                          second: 'HH:mm:ss'
                      },
                  },
                  ticks: {
                      // autoSkip: true, // true (デフォルト) にすると、ラベルが重ならないように自動でスキップされます
                      // maxTicksLimit: 10 // 表示するティックの最大数
                  }
              },
              y: {
                  beginAtZero: true
              }
          }
        }}
      />
    </div>
  )
}

export default ScoreGraph
